import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import OakLogo from "@/components/OakLogo";
import LoadingScreen from "@/components/LoadingScreen";
import { Sparkles, Brain, ArrowRight, Coins, FileQuestion, PlayCircle, Shield, MessageCircle, MessageCircleQuestion, BookOpen } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import QuillMascot from "@/components/QuillMascot";
import LivePreviewShowcase from "@/components/LivePreviewShowcase";
import LandingFeatureGrid from "@/components/LandingFeatureGrid";
import Footer from "@/components/Footer";
import { pickQuillLine } from "@/lib/quillLines";

// Shared key — `GuideChatGate` consumes this on mount after auth and fires
// `oak:open-guide` with the prefill, then deletes the key.
const GUIDE_INTENT_KEY = "oak_guide_pending_prefill";
const DEMO_PREFILL = "What can OAK do for me?";

export default function Landing() {
  const { user, initialSessionFetched } = useAuth();
  const navigate = useNavigate();
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    // Once hydration is done, bounce signed-in users straight to Hub so
    // /login can never keep an already-authenticated user hostage.
    if (initialSessionFetched && user) navigate("/", { replace: true });
  }, [user, initialSessionFetched, navigate]);

  // Reveal the sticky CTA either when (a) the visitor scrolls past 100px,
  // or (b) they've dwelled on the page for ~7s without engaging. Whichever
  // fires first. Both signal "this person didn't bounce immediately, give
  // them a one-tap path forward."
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 100) setShowStickyCta(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    const dwellTimer = setTimeout(() => setShowStickyCta(true), 7000);
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(dwellTimer);
    };
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Persist the demo intent and route through Google auth. The visitor
  // lands back on /, RootGate routes them to /academy, and `GuideChatGate`
  // immediately auto-opens the Guide with the seeded question. End-to-end
  // a brand-new user goes from "what is this?" → live AI conversation in
  // ~6 seconds with no extra clicks.
  const claimAssistant = () => {
    try { localStorage.setItem(GUIDE_INTENT_KEY, DEMO_PREFILL); } catch {}
    login();
  };

  // Static greeting — picked once on mount so the mascot doesn't change
  // its mind every re-render.
  const greeting = useMemo(() => pickQuillLine("greeter"), []);

  if (!initialSessionFetched) return <LoadingScreen label="Warming up OAK…" />;

  return (
    <main className="min-h-screen relative" data-testid="landing-page">
      {/* Simple top bar */}
      <nav className="px-6 md:px-10 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <OakLogo size={34} glow />
          <div className="font-display font-semibold text-[17px]">Oak AI Campus</div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/explore"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-white/75 hover:text-white hover:bg-white/[0.05] font-display font-medium transition"
            data-testid="btn-explore-top"
          >
            Explore <ArrowRight size={12} />
          </Link>
          <button
            onClick={login}
            className="btn-primary px-4 py-2 rounded-lg text-sm font-semibold font-display"
            data-testid="btn-login-top"
          >
            Sign in with Google
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 md:px-10 max-w-7xl mx-auto pt-10 md:pt-20 pb-12 md:pb-28 relative">
        {/* Quill greeter — small floating mascot above the eyebrow.
            First-time visitors meet the brand mascot before reading
            anything else, anchoring brand recall. */}
        <div className="mb-6 flex items-center gap-3 animate-fadeup" data-testid="landing-quill-greeter">
          <QuillMascot size="lg" speech={greeting} testid="landing-quill" />
        </div>

        <div className="label-eyebrow mb-5 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[#00FF9D] animate-pulse" />
          V1 · AI-guided mastery
        </div>
        <h1 className="font-display font-semibold tracking-tight text-[44px] sm:text-6xl md:text-7xl leading-[0.98] max-w-4xl">
          Master anything.
          <br />
          <span className="bg-gradient-to-r from-[#00FF9D] via-[#0044FF] to-[#FF7B00] bg-clip-text text-transparent">
            Powered by Oak AI Campus.
          </span>
        </h1>
        <p className="mt-6 text-white/60 max-w-xl text-lg leading-relaxed">
          A cinematic, bento-box classroom. Tell it your goal — get a 5-step path, contextual doubts cleared, and adaptive exams that earn you credits as you grow.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <button
            onClick={login}
            className="btn-primary px-6 py-3.5 rounded-xl font-display font-semibold flex items-center gap-2"
            data-testid="btn-login-hero"
          >
            Start learning free <ArrowRight size={16} />
          </button>
          <button
            onClick={claimAssistant}
            className="px-5 py-3.5 rounded-xl border border-[#00FF9D]/45 bg-[#00FF9D]/[0.06] hover:bg-[#00FF9D]/[0.12] text-[#9bffcc] hover:text-[#00FF9D] text-sm font-display font-semibold flex items-center gap-2 transition"
            data-testid="btn-claim-assistant"
            title="Open OAK Guide and ask anything"
          >
            <MessageCircle size={14} /> Claim your AI study assistant
          </button>
          <Link
            to="/explore"
            className="px-5 py-3.5 rounded-xl border border-white/15 hover:border-white/35 bg-white/[0.04] hover:bg-white/[0.08] text-white/80 hover:text-white text-sm font-display font-semibold flex items-center gap-2 transition"
            data-testid="btn-explore-hero"
          >
            <BookOpen size={14} /> Explore live previews
          </Link>
          <Link
            to="/privacy"
            className="px-5 py-3.5 rounded-xl border border-white/10 text-white/70 hover:text-white hover:border-white/20 text-sm flex items-center gap-2"
            data-testid="link-how-it-works"
          >
            <Shield size={14} /> How we handle your data
          </Link>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-6 gap-5">
          <Tile className="md:col-span-3 glow-cobalt" testid="feature-counselor">
            <Brain className="text-[#8aa8ff]" />
            <Eye text="AI Counselor" />
            <Big>Goal → 5-step path.</Big>
            <Sub>Structured JSON plan with objectives, timing, and key terms.</Sub>
          </Tile>
          <Tile className="md:col-span-3 glow-mint" testid="feature-doubt-solver">
            <MessageCircleQuestion className="text-[#00FF9D]" />
            <Eye text="Live Doubt Solver" />
            <Big>An AI tutor that knows your course.</Big>
            <Sub>Sidebar chat grounded in your exact whitepaper. 5 doubts per credit · free on Limitless.</Sub>
          </Tile>
          <Tile className="md:col-span-2" testid="feature-exams">
            <FileQuestion className="text-[#0044FF]" />
            <Eye text="Adaptive Exams" />
            <Big>5 MCQs · from any step.</Big>
            <Sub>Every exam costs 1 credit. Results logged to history.</Sub>
          </Tile>
          <Tile className="md:col-span-2" testid="feature-explain">
            <Sparkles className="text-[#b9a8ff]" />
            <Eye text="Whitepapers" />
            <Big>30-min deep dives.</Big>
            <Sub>Theory · Case Studies · Strategy. Regenerate any module any time.</Sub>
          </Tile>
          <Tile className="md:col-span-2" testid="feature-credits">
            <Coins className="text-[#00FF9D]" />
            <Eye text="Wallet" />
            <Big>Credits that matter.</Big>
            <Sub>6 free on signup, +2 weekly. Upgrade for Limitless any time.</Sub>
          </Tile>
          <Tile className="md:col-span-6" testid="feature-ads">
            <PlayCircle className="text-[#ffb067]" />
            <Eye text="Earn credits" />
            <Big>2–15 per survey · referrals · streaks.</Big>
            <Sub>Up to 20 earned credits per week. Credits land instantly. No intrusive ads.</Sub>
          </Tile>
        </div>
      </section>

      <LivePreviewShowcase />

      {/* Iter80 — Full pre-login feature surface. "What Oak AI Campus
          can do for you" hero + welcome video side-by-side, then ALL
          33 capability cards in an animated bento grid. Replaces the
          old carousel UX so visitors see every feature at once on
          first scroll (better marketing impression-hit than a 3-card
          rotation). */}
      <LandingFeatureGrid onLogin={() => navigate("/login")} />

      {/* Unified app footer — same content as the post-login pages so the
          legal/branding/infra disclosure is consistent across the entire
          marketing → product flow. (Iter55 fix: previously the landing
          shipped a tiny custom footer that diverged from the rest of the
          app.) */}
      <Footer />

      {/* Sticky on-scroll bounce-saver. Appears after the visitor scrolls
          past the hero — catches scroll-readers right before they bounce
          and gives them a one-tap path to a live AI conversation. */}
      {showStickyCta && (
        <button
          type="button"
          onClick={claimAssistant}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] inline-flex items-center gap-2 rounded-full px-5 py-3 bg-[#0A192F] hover:bg-[#0A192F]/85 text-[#9bffcc] hover:text-[#00FF9D] border border-[#00FF9D]/45 shadow-[0_18px_50px_-18px_rgba(0,255,157,0.55)] font-display font-semibold text-[13px] animate-fadeup transition"
          data-testid="sticky-claim-assistant"
        >
          <MessageCircle size={14} className="text-[#00FF9D]" />
          Claim your AI study assistant
          <ArrowRight size={13} />
        </button>
      )}
    </main>
  );
}

function Tile({ children, className = "", testid }) {
  return (
    <div
      className={`glass p-6 md:p-7 flex flex-col gap-2 transition-transform hover:-translate-y-1 ${className}`}
      data-testid={testid}
    >
      {children}
    </div>
  );
}
function Eye({ text }) { return <div className="label-eyebrow mt-1">{text}</div>; }
function Big({ children }) { return <div className="font-display text-xl md:text-2xl tracking-tight">{children}</div>; }
function Sub({ children }) { return <div className="text-white/55 text-sm leading-relaxed">{children}</div>; }
